import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
  Paper,
  CircularProgress,
  TextField,
  MenuItem
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { getListDevice } from 'src/features/deviceSlice';
import { getListDeviceType } from 'src/features/deviceTypeSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
  filterInput: {
    backgroundColor: 'white',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8
    },
    '& .MuiInputLabel-root': {
      fontSize: 13
    }
  },
  tableContainer: {
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    overflow: 'hidden'
  },
  tableHead: {
    backgroundColor: '#f8fafc',
    '& th': {
      fontWeight: 700,
      color: '#475461',
      fontSize: '13px',
      borderBottom: '1px solid #e0e3ef'
    }
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f1f5f9 !important'
    }
  },
  tableCell: {
    fontSize: '14px',
    color: '#1e293b',
    padding: '16px',
    borderBottom: '1px solid #f1f5f9'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3)
  },
  emptyState: {
    padding: theme.spacing(10),
    textAlign: 'center',
    color: '#64748b'
  }
}));

const InventoryTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [deviceTypeId, setDeviceTypeId] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const PAGE_SIZE = 12;

  const { listDevice, totalDevice, isLoading } = useSelector(state => state.deviceSlice);
  const { listDeviceType } = useSelector(state => state.deviceTypeSlice);
  const { dataLogin } = useSelector(state => state.authSlice);

  const currentAgencyId = dataLogin?.agency_id || 1;

  useEffect(() => {
    dispatch(getListDeviceType());
  }, [dispatch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchKeyword(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    dispatch(getListDevice({
      page,
      page_size: PAGE_SIZE,
      agency_id: currentAgencyId,
      device_type_id: deviceTypeId === 'all' ? undefined : deviceTypeId,
      keywords: searchKeyword || undefined
    }));
  }, [dispatch, page, deviceTypeId, currentAgencyId, searchKeyword]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const totalPages = Math.ceil((totalDevice || 0) / PAGE_SIZE);

  return (
    <Box className={classes.root}>
      <Box className={classes.filterBar}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Tìm kiếm Serial"
          className={classes.filterInput}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ minWidth: 260 }}
        />
        <TextField
          select
          variant="outlined"
          size="small"
          className={classes.filterInput}
          value={deviceTypeId}
          onChange={(e) => {
            setDeviceTypeId(e.target.value);
            setPage(1);
          }}
          style={{ minWidth: 200 }}
          SelectProps={{
            MenuProps: {
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              },
              getContentAnchorEl: null,
              PaperProps: {
                style: {
                  marginTop: 8,
                  maxHeight: 400
                }
              }
            }
          }}
        >
          <MenuItem value="all">Tất cả loại thiết bị</MenuItem>
          {listDeviceType && listDeviceType.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.name}
            </MenuItem>
          ))}
        </TextField>

        <Box ml="auto">
          <Typography variant="subtitle2" style={{ color: '#475461', fontWeight: 600 }}>
            Tổng số: <span style={{ color: '#D9042F', fontSize: '18px' }}>{totalDevice || 0}</span> thiết bị
          </Typography>
        </Box>
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell align="center" style={{ width: 60 }}>STT</TableCell>
              <TableCell>Loại thiết bị</TableCell>
              <TableCell>Serial</TableCell>
              <TableCell>Imei</TableCell>
              <TableCell>Sim</TableCell>
              <TableCell>Phiên bản</TableCell>
              <TableCell>Phiên bản firmware</TableCell>
              <TableCell>Ngày sản xuất</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listDevice && listDevice.length > 0 ? (
              listDevice.map((device, index) => (
                <TableRow key={device.id} className={classes.tableRow} style={{ opacity: isLoading ? 0.6 : 1 }}>
                  <TableCell align="center" className={classes.tableCell}>
                    {(page - 1) * PAGE_SIZE + index + 1}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {device?.deviceType?.name || '--'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {device.serial || '--'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {device.imei || '--'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {device.sim || '--'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {device.hardware_version || '--'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {device.firmware_version || '--'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {device.date_of_manufacture
                      ? moment(device.date_of_manufacture).format('DD/MM/YYYY')
                      : '--'}
                  </TableCell>
                </TableRow>
              ))
            ) : isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className={classes.emptyState}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    Đang tải dữ liệu...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell colSpan={8} className={classes.emptyState}>
                  <Typography variant="body1">Không có thiết bị trong kho</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {listDevice && listDevice.length > 0 && (
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          style={{ width: '100%' }}
          p={2}
          borderTop="1px solid #eee"
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}
          >
            <Typography style={{ fontSize: '14px', color: '#475461', fontWeight: 500 }}>
              Tổng: {totalDevice || 0}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="small"
              siblingCount={1}
              boundaryCount={2}
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default InventoryTable;
